import React from 'react';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';

import {useExperimentsStore} from 'apps/experiments/src/store';
import {Experiment, ExperimentUpdates} from 'iron/apps/experiments/src/types';

import {PageTrackingEvent, trackExperimentPageAction} from '../tracking';
import {DEFAULT_EXPERIMENT} from '../utils';
import {useUpdateExperiment} from './update-experiment';

export const DRAFT_KEYS = [`name`, `hypothesis`, `settings`, `metrics`, `start_date`, `end_date`] as const;

export function useStagedExperiment() {
  const experiment = useExperimentsStore((state) => state.experiment);
  const draftExperiment = useExperimentsStore((state) => state.draftExperiment);
  const setDraftExperiment = useExperimentsStore((state) => state.setDraftExperiment);
  const updateExperiment = useUpdateExperiment();

  const hasStagedChanges =
    !isEmpty(draftExperiment) && !isEqual(pick(draftExperiment, DRAFT_KEYS), pick(experiment, DRAFT_KEYS));

  const stagedExperiment = React.useMemo(() => {
    const draftValues = pickBy(draftExperiment, (value, key) => {
      return value !== undefined && DRAFT_KEYS.includes(key as (typeof DRAFT_KEYS)[number]);
    });
    return {
      ...DEFAULT_EXPERIMENT,
      ...(experiment ?? {}),
      ...draftValues,
      settings: {
        ...DEFAULT_EXPERIMENT.settings,
        ...experiment?.settings,
        ...draftExperiment?.settings,
      },
    } satisfies Experiment;
  }, [experiment, draftExperiment]);

  const stageChanges = React.useCallback(
    (updates: ExperimentUpdates) => {
      const newDraft = {
        ...draftExperiment,
        ...updates,
        settings: {
          ...(draftExperiment?.settings ?? {}),
          ...updates.settings,
        },
      };
      setDraftExperiment(newDraft);
    },
    [draftExperiment, setDraftExperiment],
  );

  const commitExperiment = React.useCallback(() => {
    if (hasStagedChanges) {
      // only include staged changes settings that are defined in the draft and are part of the DRAFT_KEYS
      const updates = pick(stagedExperiment, DRAFT_KEYS) satisfies ExperimentUpdates;
      updateExperiment(updates, {optimistic: true});
      setDraftExperiment(undefined);
      trackExperimentPageAction({
        event: PageTrackingEvent.CommitExperiment,
        experiment: stagedExperiment,
      });
    }
  }, [stagedExperiment, hasStagedChanges, updateExperiment, setDraftExperiment]);

  const revertExperiment = React.useCallback(() => {
    if (hasStagedChanges) {
      setDraftExperiment(undefined);
      trackExperimentPageAction({
        event: PageTrackingEvent.RevertExperiment,
        experiment: stagedExperiment,
      });
    }
  }, [stagedExperiment, hasStagedChanges, setDraftExperiment]);

  return {
    stagedExperiment,
    hasStagedChanges,
    stageChanges,
    commitExperiment,
    revertExperiment,
  };
}
